import React, { useState, useRef, DragEvent, ClipboardEvent } from 'react';
import { X, UploadCloud, Download, Image as ImageIcon, Wand2, Zap, AlertTriangle } from 'lucide-react';
import { useImageRemoval } from '../hooks/useImageRemoval';

interface ImageRemoverModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ImageRemoverModal({ isOpen, onClose }: ImageRemoverModalProps) {
  const { status, previewUrl, resultUrl, metadata, error, removeBackground, reset } = useImageRemoval();
  const [model, setModel] = useState('u2net');
  const [preferTier, setPreferTier] = useState<'tier1-mps' | 'tier2-cpu'>('tier1-mps');
  const [isDragging, setIsDragging] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      removeBackground(e.dataTransfer.files[0], { model, preferTier });
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    if (e.clipboardData.files && e.clipboardData.files.length > 0) {
      removeBackground(e.clipboardData.files[0], { model, preferTier });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      removeBackground(e.target.files[0], { model, preferTier });
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleDownload = async () => {
    if (resultUrl) {
      try {
        const response = await fetch(resultUrl);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `removed-bg-${Date.now()}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Failed to download image', err);
      }
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      <div 
        className="relative w-full max-w-4xl bg-zinc-950/90 backdrop-blur-xl border border-zinc-800/80 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        onPaste={handlePaste}
        tabIndex={0} // for paste event
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800/80">
          <div className="flex items-center gap-3 text-zinc-100">
            <div className="p-2 bg-indigo-500/10 rounded-lg">
              <Wand2 className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold leading-tight">AI Background Remover</h2>
              <p className="text-xs text-zinc-400">Zero-knowledge secure inference</p>
            </div>
          </div>
          <button 
            onClick={handleClose}
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800/50 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex flex-col gap-6">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <p>{error}</p>
            </div>
          )}

          {status === 'idle' && (
            <div 
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={triggerFileSelect}
              className={`
                w-full h-80 border-2 border-dashed rounded-xl flex flex-col items-center justify-center gap-4 cursor-pointer transition-colors
                ${isDragging ? 'border-indigo-500 bg-indigo-500/5' : 'border-zinc-700 hover:border-zinc-500 hover:bg-zinc-900/50'}
              `}
            >
              <div className="p-4 bg-zinc-900 rounded-full">
                <UploadCloud className="w-8 h-8 text-zinc-400" />
              </div>
              <div className="text-center">
                <p className="text-zinc-200 font-medium">Click to upload or drag and drop</p>
                <p className="text-sm text-zinc-500 mt-1">or paste from clipboard (PNG, JPG, WEBP)</p>
              </div>
              <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleFileChange} 
                className="hidden" 
                accept="image/png, image/jpeg, image/webp"
              />
            </div>
          )}

          {(status === 'uploading' || status === 'processing') && (
            <div className="w-full h-80 border border-zinc-800 rounded-xl flex flex-col items-center justify-center gap-6 bg-zinc-900/30">
              <div className="relative">
                {previewUrl && (
                  <img src={previewUrl} alt="Preview" className="w-32 h-32 object-cover rounded-lg opacity-50 blur-sm" />
                )}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-10 h-10 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
                </div>
              </div>
              <div className="text-center">
                <p className="text-zinc-200 font-medium">{status === 'uploading' ? 'Uploading Image...' : 'AI is removing background...'}</p>
                <p className="text-sm text-zinc-500 mt-1">Please wait while inference runs</p>
              </div>
            </div>
          )}

          {status === 'completed' && (
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <span className="text-xs font-medium text-zinc-400 uppercase tracking-wider">Original</span>
                  <div className="relative w-full aspect-square border border-zinc-800 rounded-xl overflow-hidden bg-zinc-900">
                    {previewUrl && <img src={previewUrl} alt="Original" className="w-full h-full object-contain" />}
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-xs font-medium text-zinc-400 uppercase tracking-wider">Result (Transparent)</span>
                  <div 
                    className="relative w-full aspect-square border border-zinc-800 rounded-xl overflow-hidden"
                    style={{
                      background: 'repeating-conic-gradient(#27272a 0% 25%, #18181b 0% 50%) 50% / 20px 20px'
                    }}
                  >
                    {resultUrl && <img src={resultUrl} alt="Result" className="w-full h-full object-contain" />}
                  </div>
                </div>
              </div>

              {metadata && (
                <div className="flex flex-wrap items-center gap-3 p-3 bg-zinc-900/50 rounded-lg border border-zinc-800">
                  {metadata.tier === 'tier1-mps' && (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-500/10 text-amber-400 rounded-md text-xs font-medium border border-amber-500/20">
                      <Zap className="w-3.5 h-3.5" />
                      <span>Apple Silicon MPS ({metadata.durationMs}ms)</span>
                    </div>
                  )}
                  {metadata.degraded && (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 bg-red-500/10 text-red-400 rounded-md text-xs font-medium border border-red-500/20">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      <span>Fallback CPU Mode</span>
                    </div>
                  )}
                  {metadata.estimatedCredits !== undefined && (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-500/10 text-emerald-400 rounded-md text-xs font-medium border border-emerald-500/20">
                      <span>Attestation: +{metadata.estimatedCredits.toFixed(4)} cr</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Controls */}
          {status === 'idle' && (
            <div className="border-t border-zinc-800/80 pt-4">
              <button 
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-xs font-medium text-zinc-400 hover:text-zinc-200 transition-colors w-full text-left mb-3"
              >
                <span>{showAdvanced ? '▼' : '▶'}</span>
                Advanced Options
              </button>
              
              {showAdvanced && (
                <div className="flex items-center gap-4 bg-zinc-900/50 p-3 rounded-lg border border-zinc-800">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-zinc-400 mb-1">AI Model</label>
                    <select 
                      value={model} 
                      onChange={(e) => setModel(e.target.value)}
                      className="w-full bg-zinc-900 border border-zinc-700 text-zinc-200 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 p-2"
                    >
                      <option value="u2net">U2Net (General)</option>
                      <option value="u2netp">U2Netp (Fast)</option>
                      <option value="u2net_human_seg">U2Net Human Seg</option>
                      <option value="u2net_cloth_seg">U2Net Cloth Seg</option>
                      <option value="silueta">Silueta (Small/Fast)</option>
                      <option value="isnet-general-use">ISNet (General Use)</option>
                      <option value="isnet-anime">ISNet Anime</option>
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-zinc-400 mb-1">Preferred Node</label>
                    <select 
                      value={preferTier} 
                      onChange={(e) => setPreferTier(e.target.value as any)}
                      className="w-full bg-zinc-900 border border-zinc-700 text-zinc-200 text-sm rounded-lg focus:ring-indigo-500 focus:border-indigo-500 p-2"
                    >
                      <option value="tier1-mps">Tier 1 (Apple Silicon MPS)</option>
                      <option value="tier2-cpu">Tier 2 (CPU Container)</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          )}

          {(status === 'completed' || status === 'failed') && (
            <div className="flex justify-end items-center gap-3 border-t border-zinc-800/80 pt-4">
              <button 
                onClick={reset}
                className="px-4 py-2 text-sm font-medium text-zinc-300 hover:text-white transition-colors"
              >
                Start Over
              </button>
              {status === 'completed' && (
                <button 
                  onClick={handleDownload}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2 shadow-lg shadow-indigo-500/20"
                >
                  <Download className="w-4 h-4" />
                  Download PNG
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
